import { parseISO } from "date-fns"
import { type TimelineChain } from "@/components/timeline/buildTimelineData"
import { RouteStop, shouldAlert } from "@/reducers/routes2gSlice"
import { DAY_IN_MS } from "./plannerGeometry"
import { resolveCountryCode } from "./vehicleSuggestions"

export type TimeSegment = { startOffset: number; endOffset: number }

export type RouteBarSegment = TimeSegment & {
  /** The stops this segment actually spans — not the whole route. */
  label: string
  stopCount: number
  /** Those same stops, for the dialog behind the bar. */
  stops: RouteStop[]
  /** Late because of a stop inside this segment, not anywhere on the row. */
  isLate: boolean
  startCountryCode?: string
  endCountryCode?: string
}

export type RouteBar = {
  // Route time NOT already covered by provider chains (dedup: synced feeds
  // land in the Slick route too, and would otherwise render twice)
  segments: RouteBarSegment[]
}

type TimedStop = { stop: RouteStop; offset: number }

// A route stop and the chain event for the same job rarely carry times to the
// second (the route keeps its own copy, the feed re-reads the TMS), so a stop
// counts as "the chains already draw this" when it lands within half an hour
// of a chain's span rather than exactly inside it.
const CHAIN_MATCH_TOLERANCE_DAYS = 1 / 48

const parseTime = (value?: string): Date | undefined => {
  if (!value) return undefined
  const parsed = parseISO(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

const toOffset = (date: Date, timelineStart: Date): number =>
  (date.getTime() - timelineStart.getTime()) / DAY_IN_MS

// Subtracts blocked intervals from [start, end]; keeps leftovers >= minLength
export const subtractIntervals = (
  startOffset: number,
  endOffset: number,
  blocks: TimeSegment[],
  minLength: number,
): TimeSegment[] => {
  let segments: TimeSegment[] = [{ startOffset, endOffset }]
  for (const block of blocks) {
    const next: TimeSegment[] = []
    for (const segment of segments) {
      if (
        block.endOffset <= segment.startOffset ||
        block.startOffset >= segment.endOffset
      ) {
        next.push(segment)
        continue
      }
      if (block.startOffset > segment.startOffset) {
        next.push({
          startOffset: segment.startOffset,
          endOffset: block.startOffset,
        })
      }
      if (block.endOffset < segment.endOffset) {
        next.push({
          startOffset: block.endOffset,
          endOffset: segment.endOffset,
        })
      }
    }
    segments = next
  }
  return segments.filter(
    (segment) => segment.endOffset - segment.startOffset >= minLength,
  )
}

const stopLabel = (stop: RouteStop) =>
  stop.location.locality || stop.location.formattedAddress || "?"

const describeStops = (stops: TimedStop[]): RouteBarSegment | null => {
  const first = stops[0]
  const last = stops[stops.length - 1]
  if (!first || !last) return null
  return {
    startOffset: 0,
    endOffset: 0,
    label:
      stops.length === 1
        ? stopLabel(first.stop)
        : `${stopLabel(first.stop)} → ${stopLabel(last.stop)}`,
    stopCount: stops.length,
    stops: stops.map((entry) => entry.stop),
    isLate: stops.some((entry) => shouldAlert(entry.stop)),
    startCountryCode: resolveCountryCode(first.stop.location),
    endCountryCode: resolveCountryCode(last.stop.location),
  }
}

/**
 * The vehicle's current Slick route (stops already sent to the driver) as
 * occupancy bars — so vehicles whose work isn't in any provider feed still
 * visibly occupy time on the planner.
 *
 * Only the parts of the route the provider chains don't already draw, and only
 * where the route has stops of its own to show for it: the leftover between two
 * transports of the same route says nothing a dispatcher can't read off the
 * chains, and used to render as a bar labelled with the whole route.
 */
export const buildRouteBar = (
  stops: RouteStop[] | undefined,
  timelineStart: Date,
  chains: TimelineChain[],
): RouteBar | null => {
  const timedStops = (stops ?? [])
    .map((stop) => ({
      stop,
      time:
        parseTime(stop.scheduled_arrival_time) ??
        parseTime(stop.estimated_arrival_time_with_tacho) ??
        parseTime(stop.estimated_arrival_time),
    }))
    .filter((entry): entry is { stop: RouteStop; time: Date } =>
      Boolean(entry.time),
    )
    .sort((left, right) => left.time.getTime() - right.time.getTime())
    .map(({ stop, time }) => ({ stop, offset: toOffset(time, timelineStart) }))

  if (timedStops.length === 0) return null

  const first = timedStops[0]
  const last = timedStops[timedStops.length - 1]
  // Routes finished more than a day ago are history, not occupancy.
  if (last.offset < -1) return null
  const startOffset = first.offset
  const endOffset = Math.max(last.offset, startOffset + 0.2)

  const chainSpans = chains.map((chain) => ({
    startOffset: chain.startOffset,
    endOffset: chain.endOffset,
  }))
  // Feed chains already visualize this work (synced stops land in the route
  // too) — draw only the route time the chains don't cover.
  const segments = subtractIntervals(startOffset, endOffset, chainSpans, 0.35)
  const ownStops = timedStops.filter(
    (entry) =>
      !chainSpans.some(
        (span) =>
          entry.offset >= span.startOffset - CHAIN_MATCH_TOLERANCE_DAYS &&
          entry.offset <= span.endOffset + CHAIN_MATCH_TOLERANCE_DAYS,
      ),
  )

  const barSegments = segments.flatMap((segment) => {
    const segmentStops = ownStops.filter(
      (entry) =>
        entry.offset >= segment.startOffset &&
        entry.offset <= segment.endOffset,
    )
    const described = describeStops(segmentStops)
    return described ? [{ ...described, ...segment }] : []
  })
  if (barSegments.length === 0) return null

  return { segments: barSegments }
}
